import reducerRegistry from '@comeon/reducer-registry';
import type { State, MultiGameActions } from './types';
import type { Game } from '~/components/casino-shared/types';
import {
	type CasinoSharedAction,
	type CasinoSharedRootState,
	type CasinoSharedThunkAction
} from '~/components/casino-shared/ducks/types';
import { graphyteSearch } from '~/components/search/ducks/graphyte';
import getGameUrls from '~/components/casino-shared/utilities/getGameUrls';
import { currentGameSelector } from '~/components/casino-shared/game-loader/ducks/selectors';

const REDUCER_NAME = 'multiGame';

const ACTION_PREFIX = '@komigen/mobile-frontend/multi-game';
export const SET_IS_MODAL_SHOWN = `${ACTION_PREFIX}/SET_IS_MODAL_SHOWN`;
export const SET_SEARCH_KEYWORD = `${ACTION_PREFIX}/SET_SEARCH_KEYWORD`;
export const SET_SEARCH_LOADING = `${ACTION_PREFIX}/SET_SEARCH_LOADING`;
export const SET_REMOTE_SEARCH_RESULTS = `${ACTION_PREFIX}/SET_REMOTE_SEARCH_RESULTS`;
export const CLEAR_SEARCH_STATE = `${ACTION_PREFIX}/CLEAR_SEARCH_STATE`;

const initialState: State = {
	isModalShown: false,
	searchKeyword: '',
	searchResults: [],
	isSearchLoading: false,
	latestSearchId: ''
};

// selectors
export const isModalShownSelector = (state: CasinoSharedRootState): State['isModalShown'] =>
	(state[REDUCER_NAME] as State).isModalShown;
export const searchKeywordSelector = (state: CasinoSharedRootState): State['searchKeyword'] =>
	(state[REDUCER_NAME] as State).searchKeyword;
export const searchResultsSelector = (state: CasinoSharedRootState): State['searchResults'] =>
	(state[REDUCER_NAME] as State).searchResults;
export const isSearchLoadingSelector = (state: CasinoSharedRootState): State['isSearchLoading'] =>
	(state[REDUCER_NAME] as State).isSearchLoading;

// action creators
export const setIsModalShown = (payload: boolean): CasinoSharedAction => {
	return {
		type: SET_IS_MODAL_SHOWN,
		payload
	};
};

export const setSearchKeyword = (payload: string): CasinoSharedAction => {
	return {
		type: SET_SEARCH_KEYWORD,
		payload
	};
};

export const setSearchLoading = (payload: boolean): CasinoSharedAction => {
	return {
		type: SET_SEARCH_LOADING,
		payload
	};
};

const setRemoteSearchResults = ({
	results = [],
	searchId = '',
	pending
}: {
	results?: State['searchResults'];
	searchId?: string;
	pending: boolean;
}): CasinoSharedAction => ({
	type: SET_REMOTE_SEARCH_RESULTS,
	payload: {
		searchResults: results,
		latestSearchId: searchId,
		isSearchLoading: pending
	}
});

export const clearSearch = (): CasinoSharedAction => {
	return {
		type: CLEAR_SEARCH_STATE
	};
};

// thunks
export const fetchSearchResults =
	(keyword: string): CasinoSharedThunkAction =>
	async (dispatch, getState, { imageDescriptors, imageFormats, license }) => {
		dispatch(setRemoteSearchResults({ pending: true }));
		const state = getState();
		const currentGame = currentGameSelector(state);

		try {
			const data = await graphyteSearch(keyword, imageDescriptors, imageFormats, license);

			if (data?.results?.length) {
				data.results = data.results
					.filter(r => r.code && r.code !== currentGame?.gameCode)
					.filter(r => r.code && r.categoryCode === 'CASINO_LIVE_CASINO')
					.map(r => {
						const { realModeUrl } = getGameUrls(
							{ code: r.code as Game['code'], categoryCode: r.categoryCode as Game['categoryCode'] },
							license
						);

						return { ...r, realModeUrl };
					});
			}

			dispatch(setRemoteSearchResults(data));
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error fetching search results:', error);
		} finally {
			dispatch(setSearchLoading(false));
		}
	};

// TODO: use in search results container

// const searchConversion = (gameCode: string): CasinoSharedThunkAction => {
// 	return (_dispatch, getState) => {
// 		const {
// 			brand: { graphyteSearchEnabled },
// 			multiGame: { searchResults }
// 		} = getState();
//
// 		if (graphyteSearchEnabled && searchResults?.length) {
// 			const {
// 				multiGame: { searchKeyword, latestSearchId }
// 			} = getState();
// 			graphyteSearchConversion(gameCode, latestSearchId, searchKeyword);
// 		}
// 	};
// };

// reducers START
function reducer(state = initialState, { type, payload }: MultiGameActions) {
	switch (type) {
		case SET_IS_MODAL_SHOWN:
			return { ...state, isModalShown: payload };
		case SET_SEARCH_KEYWORD:
			return { ...state, searchKeyword: payload };
		case SET_SEARCH_LOADING:
			return { ...state, isSearchLoading: payload };
		case SET_REMOTE_SEARCH_RESULTS:
			return { ...state, ...(payload as Partial<State>) };
		case CLEAR_SEARCH_STATE:
			return { ...state, searchKeyword: '', searchResults: [], isSearchLoading: false };
		default:
			return state;
	}
}
// reducers END

reducerRegistry.register(REDUCER_NAME, reducer);

export default reducer;
